import { NgModule } from '@angular/core';
import { Routes, RouterModule } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Location } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { LocalizeRouterModule, LocalizeRouterSettings, LocalizeParser } from '@gilsdav/ngx-translate-router';
import { LocalizeRouterHttpLoader } from '@gilsdav/ngx-translate-router-http-loader';
import { AuthGuard } from '@core/guards';

// tslint:disable-next-line: typedef
export function HttpLoaderFactory(
    translate: TranslateService,
    location: Location,
    settings: LocalizeRouterSettings,
    http: HttpClient
) {
    return new LocalizeRouterHttpLoader(translate, location, settings, http);
}

const routes: Routes = [
    {
        path: '',
        redirectTo: 'auth/login',
        pathMatch: 'full'
    },
    {
        path: 'auth',
        loadChildren: () => import('./modules/auth/auth.module').then((m) => m.AuthModule)
    },
    {
        path: 'profile',
        loadChildren: () => import('./modules/profile/profile.module').then((m) => m.ProfileModule),
        pathMatch: 'full',
        canActivate: [AuthGuard]
    },
    {
        path: 'customer',
        children: [
            {
                path: 'user',
                loadChildren: () => import('./modules/user/user.module').then((m) => m.UserModule)
            },
            {
                path: 'contact',
                loadChildren: () =>
                    import('./modules/contact/contact-management.module').then((m) => m.ContactManagementModule)
            },
            {
                path: 'account',
                loadChildren: () =>
                    import('./modules/account/account-management.module').then((m) => m.AccountManagementModule)
            }
        ],
        canActivate: [AuthGuard]
    },
    {
        path: 'trading-account',
        children: [
            {
                path: 'account',
                loadChildren: () =>
                    import('./modules/trading-account/trading-account.module').then((m) => m.TradingAccountModule)
            },
            {
                path: 'request',
                loadChildren: () =>
                    import('./modules/trading-account-request/trading-account-request.module').then(
                        (m) => m.TradingAccountRequestModule
                    )
            }
        ],
        canActivate: [AuthGuard]
    },
    {
        path: 'deposit',
        loadChildren: () =>
            import('./modules/deposit/deposit-management.module').then((m) => m.DepositManagementModule),
        canActivate: [AuthGuard]
    },
    {
        path: 'transfer',
        loadChildren: () => import('./modules/transfer/transfer.module').then((m) => m.TransferModule),
        canActivate: [AuthGuard]
    },
    {
        path: 'b2b-transfer',
        loadChildren: () => import('./modules/b2b-transfer/b2b-transfer.module').then((m) => m.B2BTransferModule),
        canActivate: [AuthGuard]
    },
    {
        path: 'withdrawal',
        loadChildren: () => import('./modules/withdrawal/withdrawal.module').then((m) => m.WithdrawalModule),
        canActivate: [AuthGuard]
    },
    {
        path: 'funding-method',
        loadChildren: () => import('./modules/funding-method/funding-method.module').then((m) => m.FundingMethodModule),
        canActivate: [AuthGuard]
    },
    {
        path: 'payout-sheets',
        loadChildren: () => import('./modules/payout-sheets/payout-sheets.module').then((m) => m.PayoutSheetsModule),
        canActivate: [AuthGuard]
    },
    {
        path: 'report',
        loadChildren: () => import('./modules/report/report.module').then((m) => m.ReportModule),
        canActivate: [AuthGuard]
    },
    {
        path: 'monitoring',
        loadChildren: () => import('./modules/monitoring/monitoring.module').then((m) => m.MonitoringModule),
        canActivate: [AuthGuard]
    },
    {
        path: 'leverage',
        loadChildren: () => import('./modules/request/request.module').then((m) => m.RequestModule),
        canActivate: [AuthGuard]
    },
    {
        path: 'rewards',
        loadChildren: () => import('./modules/rewards/rewards.module').then((m) => m.RewardsModule),
        canActivate: [AuthGuard]
    },
    {
        path: 'bulk-operation',
        loadChildren: () =>
            import('./modules/special-feature/special-feature.module').then((m) => m.SpecialFeatureModule),
        canActivate: [AuthGuard]
    },
    {
        path: 'setting',
        loadChildren: () => import('./modules/setting/setting.module').then((m) => m.SettingModule),
        canActivate: [AuthGuard]
    },
    {
        path: 'application-key',
        loadChildren: () =>
            import('./modules/application-key/application-key.module').then((m) => m.ApplicationKeyModule),
        canActivate: [AuthGuard]
    },
    {
        path: '404',
        loadChildren: () => import('./modules/pages/errors/404/error-404.module').then((m) => m.Error404Module)
    },
    {
        path: 'internal-brokers',
        loadChildren: () =>
            import('./modules/internal-broker/internal-broker.module').then((m) => m.InternalBrokerManagementModule),
        canActivate: [AuthGuard]
    },
    {
        path: 'partners',
        loadChildren: () => import('./modules/partners/partners.module').then((m) => m.PartnersModule),
        canActivate: [AuthGuard]
    },
    {
        path: 'repair',
        loadChildren: () => import('./modules/repair/repair.module').then((m) => m.RepairModule),
        canActivate: [AuthGuard]
    },
    {
        path: 'promotions',
        loadChildren: () =>
            import('./modules/promotions/promotion-management.module').then((m) => m.PromotionManagementModule),
        canActivate: [AuthGuard]
    },
    {
        path: 'compliance',
        loadChildren: () => import('./modules/compliance/compliance.module').then((m) => m.ComplianceModule),
        canActivate: [AuthGuard]
    },
    {
        path: '**',
        redirectTo: '/404'
    }
];

@NgModule({
    imports: [
        RouterModule.forRoot(routes, { relativeLinkResolution: 'legacy' }),
        LocalizeRouterModule.forRoot(routes, {
            parser: {
                provide: LocalizeParser,
                useFactory: HttpLoaderFactory,
                deps: [TranslateService, Location, LocalizeRouterSettings, HttpClient]
            }
        })
    ],
    exports: [RouterModule, LocalizeRouterModule],
    providers: []
})
export class AppRoutingModule {}
